"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "@kkeut/lib/firebase/client";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";

/**
 * Firestore 의 {branchId}/menuConfig/list 컬렉션에서 active=true 메뉴를 order 순으로 로드.
 * 클러스터 B 13곳 헤더의 동적 메뉴 로딩 패턴을 hook 으로 추출.
 *
 * @param {string} branchId - 지점 ID (예: "hongdae", "bumkye")
 * @returns {{ menuItems, rootItems, childItems, loading, error, reload }}
 */
export function useMenuConfig(branchId) {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    setMenuItems([]);
    setError(null);
    if (!branchId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timeout;
    (async () => {
      try {
        setLoading(true);
        const qy = query(
          collection(db, branchId, "menuConfig", "list"),
          where("active", "==", true),
          orderBy("order", "asc"),
        );
        // 재시도 안내를 보여주되 늦게 도착한 정상 응답도 계속 수신한다.
        // 재시도 버튼이 없는 데스크톱 헤더도 네트워크 복구 후 메뉴가 나타나야 한다.
        timeout = setTimeout(() => {
          if (cancelled) return;
          setError(new Error("Menu request timed out"));
          setLoading(false);
        }, 10000);
        const snap = await getDocs(qy);
        if (cancelled) return;
        // 오프라인 캐시가 비었다고 해서 실제 메뉴가 없는 것으로 판단하지 않는다.
        if (snap.metadata?.fromCache && snap.docs.length === 0) {
          throw new Error("Menu is unavailable offline");
        }
        setMenuItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error("메뉴 불러오기 실패:", err);
        setError(err);
      } finally {
        clearTimeout(timeout);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [branchId, attempt]);

  const rootItems = useMemo(() => menuItems.filter((m) => !m.parent), [menuItems]);
  const childItems = useMemo(() => menuItems.filter((m) => m.parent), [menuItems]);

  return { menuItems, rootItems, childItems, loading, error, reload };
}
